import * as d3 from "d3";
import { SparklineData } from "../../interfaces/resource.interfaces";

export class SparklineManager {
  static globalValueMax = Number.MIN_VALUE;
  static globalValueMin = Number.MAX_VALUE;
  static globalYearMax = Number.MIN_VALUE;
  static globalYearMin = Number.MAX_VALUE;

  static updateGlobalExtremes(value: number, year: number) {
    this.globalValueMax = Math.max(this.globalValueMax, value);
    this.globalValueMin = Math.min(this.globalValueMin, value);
    this.globalYearMax = Math.max(this.globalYearMax, year);
    this.globalYearMin = Math.min(this.globalYearMin, year);
  }

  static getGlobalValueMax() {
    return this.globalValueMax;
  }

  static getGlobalValueMin() {
    return this.globalValueMin;
  }

  static getGlobalYearMax() {
    return this.globalYearMax;
  }

  static getGlobalYearMin() {
    return this.globalYearMin;
  }

  static resetGlobals() {
    this.globalValueMax = Number.MIN_VALUE;
    this.globalValueMin = Number.MAX_VALUE;
    this.globalYearMax = Number.MIN_VALUE;
    this.globalYearMin = Number.MAX_VALUE;
  }
}

export class Sparkline {
  private data: SparklineData[];
  private lineThickness: number;
  private additionalMarkers: string[];
  private additionalMarkersData: SparklineData[];
  private svg: SVGSVGElement;
  private localMax?: number;
  private localMin?: number;
  private xScale: any;
  private yScale: any;
  private line: any;

  constructor(data: SparklineData[], additionalMarkers: string[] = []) {
    this.data = data;
    this.lineThickness = 2;
    this.additionalMarkers = additionalMarkers.sort();
    this.additionalMarkersData = data.filter((d) =>
      additionalMarkers.includes(d.year.toString())
    );
    this.updateGlobalExtremes();
    this.svg = this.createSparkline()!;

    this.localMax = d3.max(data, (d) => d.indicator);
    this.localMin = d3.min(data, (d) => d.indicator);
  }

  updateGlobalExtremes() {
    this.data.forEach((d) => {
      SparklineManager.updateGlobalExtremes(d.indicator, d.year);
    });
  }

  createSparkline() {
    const margin = { top: 10, right: 10, bottom: 10, left: 10 };
    const width = 100 - margin.left - margin.right;
    const height = 100 - margin.top - margin.bottom;

    const lineThickness = this.lineThickness;
    const data = this.data;
    const additionalMarkers = this.additionalMarkers;

    const svg = d3
      .create("svg")
      .attr("width", 100)
      .attr("height", 100)
      .attr("viewBox", [
        -margin.left,
        -margin.top,
        100 + margin.right,
        100 + margin.bottom,
      ]);

    this.xScale = d3
      .scaleLinear()
      .range([0, width])
      // take the global min and max
      .domain([
        SparklineManager.getGlobalYearMin(),
        SparklineManager.getGlobalYearMax(),
      ]);

    this.yScale = d3
      .scaleLinear()
      .range([height, 0])
      // @ts-ignore
      .domain([
        d3.min(data, (d) => d.indicator),
        d3.max(data, (d) => d.indicator),
      ]);

    this.line = d3
      .line()
      .x((d: any) => this.xScale(d.year))
      .y((d: any) => this.yScale(d.indicator));

    if (additionalMarkers.length === 1) {
      svg
        .append("path")
        .datum(data)
        .attr("fill", "none")
        .attr("stroke", "black")
        .attr("stroke-width", lineThickness)
        .attr("d", this.line);
    }

    if (additionalMarkers.length === 2) {
      const [startMarker, endMarker] = additionalMarkers.sort();

      const beforeData = data.filter((d: any) => d.year <= startMarker);
      if (beforeData.length > 0) {
        svg
          .append("path")
          .datum(beforeData)
          .attr("stroke", "rgba(0,0,0,0.5)")
          .attr("fill", "none")
          .attr("stroke-width", lineThickness)
          .attr("d", this.line);
      }

      const afterData = data.filter((d: any) => d.year >= endMarker);
      if (afterData.length > 0) {
        svg
          .append("path")
          .datum(afterData)
          .attr("stroke", "rgba(0,0,0,0.5)")
          .attr("fill", "none")
          .attr("stroke-width", lineThickness)
          .attr("d", this.line);
      }

      // The before selection is before there is any data available
      if (beforeData.length === 0) {
        beforeData.push(data[0]);
      }

      if (beforeData.length > 0 && afterData.length > 0) {
        const lastBeforeDataPoint = beforeData[beforeData.length - 1];
        const firstAfterDataPoint = afterData[0];

        const connectingLine = data.filter(
          (d) =>
            d.year >= lastBeforeDataPoint.year &&
            d.year <= firstAfterDataPoint.year
        );
        svg
          .append("path")
          .datum(connectingLine)
          .attr("stroke", "rgba(0,0,0,1)")
          .attr("fill", "none")
          .attr("stroke-width", lineThickness)
          .attr("d", this.line);
      }
    }

    svg
      .append("line")
      .attr("x1", this.xScale(SparklineManager.getGlobalYearMin()))
      .attr("y1", this.yScale(data[0].indicator))
      .attr("x2", this.xScale(SparklineManager.getGlobalYearMax()))
      .attr("y2", this.yScale(data[0].indicator))
      .attr("stroke", "black")
      .attr("stroke-width", 1.5)
      .attr("stroke-dasharray", "5,5");

    svg
      .append("circle")
      .attr("cx", this.xScale(data[0].year))
      .attr("cy", this.yScale(data[0].indicator))
      .attr("r", 5)
      .attr(
        "fill",
        `rgba(0, 0, 255, ${this.determineTransparency(data[0].year)}`
      );

    svg
      .append("circle")
      .attr("cx", this.xScale(data[data.length - 1].year))
      .attr("cy", this.yScale(data[data.length - 1].indicator))
      .attr("r", 5)
      .attr(
        "fill",
        `rgba(255, 0, 0, ${this.determineTransparency(
          data[data.length - 1].year
        )}`
      );

    additionalMarkers.forEach((year) => {
      const markerData = data.find((d) => d.year.toString() === year);
      if (
        markerData &&
        markerData.year != data[0].year &&
        markerData.year != data[data.length - 1].year
      ) {
        svg
          .append("circle")
          .attr("cx", this.xScale(markerData.year))
          .attr("cy", this.yScale(markerData.indicator))
          .attr("r", 5)
          .attr("fill", "black");
      }
    });

    return svg.node();
  }

  createDetailedSVG() {
    const globalMin = SparklineManager.getGlobalValueMin();
    const globalMax = SparklineManager.getGlobalValueMax();

    const numberScale = d3
      .scaleLinear()
      .domain([globalMin, globalMax])
      .range([80, 0]);

    const svgWidth = 120; // Increased width to accommodate color scale
    const colorScaleWidth = 20; // Width of the color scale
    const chartWidth = svgWidth - colorScaleWidth; // Remaining width for the chart

    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const width = chartWidth - margin.left - margin.right;
    const height = 100 - margin.top - margin.bottom;

    const lineThickness = this.lineThickness;
    const data = this.data;
    const additionalMarkers = this.additionalMarkers;

    const svg = d3
      .create("svg")
      .attr("width", 100)
      .attr("height", 100)
      .attr("viewBox", [
        -margin.left,
        -margin.top,
        svgWidth + margin.right,
        100 + margin.bottom,
      ]);

    const svgChart = svg.append("g").attr("transform", `translate(30, 0)`);
    const svgGlobalScale = svg
      .append("g")
      .attr("id", "sparklineGlobalScale")
      .attr("transform", `translate(-20, 0)`);

    if (additionalMarkers.length === 1) {
      svgChart
        .append("path")
        .datum(data)
        .attr("fill", "none")
        .attr("stroke", "black")
        .attr("stroke-width", lineThickness)
        .attr("d", this.line);
    }

    if (additionalMarkers.length === 2) {
      const [startMarker, endMarker] = additionalMarkers.sort();

      const beforeData = data.filter((d: any) => d.year <= startMarker);
      if (beforeData.length > 0) {
        svgChart
          .append("path")
          .datum(beforeData)
          .attr("stroke", "rgba(0,0,0,0.5)")
          .attr("fill", "none")
          .attr("stroke-width", lineThickness)
          .attr("d", this.line);
      }

      const afterData = data.filter((d: any) => d.year >= endMarker);
      if (afterData.length > 0) {
        svgChart
          .append("path")
          .datum(afterData)
          .attr("stroke", "rgba(0,0,0,0.5)")
          .attr("fill", "none")
          .attr("stroke-width", lineThickness)
          .attr("d", this.line);
      }

      // The before selection is before there is any data available
      if (beforeData.length === 0) {
        beforeData.push(data[0]);
      }

      if (beforeData.length > 0 && afterData.length > 0) {
        const lastBeforeDataPoint = beforeData[beforeData.length - 1];
        const firstAfterDataPoint = afterData[0];

        const connectingLine = data.filter(
          (d) =>
            d.year >= lastBeforeDataPoint.year &&
            d.year <= firstAfterDataPoint.year
        );

        svgChart
          .append("path")
          .datum(connectingLine)
          .attr("stroke", "rgba(0,0,0,1)")
          .attr("fill", "none")
          .attr("stroke-width", lineThickness)
          .attr("d", this.line);
      }
    }

    svgChart
      .append("line")
      .attr("x1", this.xScale(SparklineManager.getGlobalYearMin()))
      .attr("y1", this.yScale(data[0].indicator))
      .attr("x2", this.xScale(SparklineManager.getGlobalYearMax()))
      .attr("y2", this.yScale(data[0].indicator))
      .attr("stroke", "black")
      .attr("stroke-width", 1.5)
      .attr("stroke-dasharray", "5,5");

    svgChart
      .append("circle")
      .attr("cx", this.xScale(data[0].year))
      .attr("cy", this.yScale(data[0].indicator))
      .attr("r", 5)
      .attr(
        `fill`,
        `rgba(0, 0, 255, ${this.determineTransparency(data[0].year)}`
      );

    svgChart
      .append("circle")
      .attr("cx", this.xScale(data[data.length - 1].year))
      .attr("cy", this.yScale(data[data.length - 1].indicator))
      .attr("r", 5)
      .attr(
        `fill`,
        `rgba(255, 0, 0, ${this.determineTransparency(
          data[data.length - 1].year
        )}`
      );

    var trapezoidGroup = svg
      .append("g")
      .attr("id", "sparklineTrapezoid")
      .attr("transform", `translate(30, 0)`);

    additionalMarkers.forEach((year) => {
      const markerData = data.find((d) => d.year.toString() === year);
      if (
        markerData &&
        markerData.year != data[0].year &&
        markerData.year != data[data.length - 1].year
      ) {
        svgChart
          .append("circle")
          .attr("cx", this.xScale(markerData.year))
          .attr("cy", this.yScale(markerData.indicator))
          .attr("r", 5)
          .attr("fill", "black");
      }
    });

    // Global Straigh Y-Line
    svgGlobalScale
      .append("line")
      .attr("x1", 22.5)
      .attr("y1", 0)
      .attr("x2", 22.5)
      .attr("y2", 80)
      .attr("stroke", "black")
      .attr("stroke-width", 2);

    //Horizonzal lines
    svgGlobalScale
      .append("line")
      .attr("x1", 17.5)
      .attr("y1", 0)
      .attr("x2", 27.5)
      .attr("y2", 0)
      .attr("stroke", "black")
      .attr("stroke-width", 2);

    svgGlobalScale
      .append("line")
      .attr("x1", 17.5)
      .attr("y1", 80)
      .attr("x2", 27.5)
      .attr("y2", 80)
      .attr("stroke", "black")
      .attr("stroke-width", 2);

    svgGlobalScale
      .append("text")
      .attr("x", 22.5)
      .attr("y", 90)
      .attr("font-size", "10px")
      .attr("text-anchor", "middle")
      .text(d3.format(",.2~f")(globalMin).replace(/,/g, "'"));

    svgGlobalScale
      .append("text")
      .attr("x", 22.5)
      .attr("y", -2.5)
      .attr("font-size", "10px")
      .attr("text-anchor", "middle")
      .text(d3.format(",.2~f")(globalMax).replace(/,/g, "'"));

    const localMin = this.localMin || 0;
    const localMax = this.localMax || 0;

    if (this.additionalMarkersData.length === 2) {
      var additionalMarkersCompleteData = data.filter(
        (d) =>
          d.year >= this.additionalMarkersData[0].year &&
          d.year <= this.additionalMarkersData[1].year
      );

      var additionalMarkerY = numberScale(
        Math.max(
          this.additionalMarkersData[0].indicator,
          this.additionalMarkersData[1].indicator
        )
      );

      var additionalMarkerX =
        this.xScale(this.additionalMarkersData[0].year) - 5;

      var additionalMarkerYMax = this.yScale(
        additionalMarkersCompleteData.reduce(
          (max, p) => (p.indicator > max ? p.indicator : max),
          additionalMarkersCompleteData[0].indicator
        )
      );

      var additionalMarkerYMin = this.yScale(
        additionalMarkersCompleteData.reduce(
          (min, p) => (p.indicator < min ? p.indicator : min),
          additionalMarkersCompleteData[0].indicator
        )
      );

      var additionalMarkerHeight = Math.abs(
        numberScale(this.additionalMarkersData[0].indicator) -
          numberScale(this.additionalMarkersData[1].indicator)
      );
      var additionalMarkersWidth = Math.abs(
        this.xScale(this.additionalMarkersData[1].year) -
          this.xScale(this.additionalMarkersData[0].year) +
          10
      );

      svgGlobalScale
        .append("rect")
        .attr("x", 20)
        .attr("y", additionalMarkerY)
        .attr("width", 5)
        .attr("height", additionalMarkerHeight)
        .attr("fill", "black");

      trapezoidGroup
        .append("rect")
        .attr("x", additionalMarkerX)
        .attr("y", additionalMarkerYMax - 5)
        .attr("width", additionalMarkersWidth)
        .attr(
          "height",
          Math.abs(additionalMarkerYMax - additionalMarkerYMin) + 10
        )
        .attr("fill", "rgba(0, 0, 0, 0)");

      trapezoidGroup
        .append("polygon")
        .attr(
          "points",
          `${additionalMarkerX},${
            additionalMarkerYMax - 5
          } ${additionalMarkerX},${additionalMarkerYMin + 5} -25, ${
            additionalMarkerY + additionalMarkerHeight
          }
           -25, ${additionalMarkerY}`
        )
        .attr("fill", "rgba(0, 0, 0, 0)");
    } else {
      svgGlobalScale
        .append("rect")
        .attr("x", 20)
        .attr("y", numberScale(localMax))
        .attr("width", 5)
        .attr("height", Math.abs(numberScale(localMin) - numberScale(localMax)))
        .attr("fill", "black");

      trapezoidGroup
        .append("rect")
        .attr("x", -5)
        .attr("y", -5)
        .attr("width", 100)
        .attr("height", 90)
        .attr("fill", "rgba(0, 0, 0, 0)");

      trapezoidGroup
        .append("polygon")
        .attr(
          "points",
          `-5,-5 -5,85 -25, ${numberScale(localMin)} -25, ${numberScale(
            localMax
          )}`
        )
        .attr("fill", "rgba(0, 0, 0, 0)");
    }

    return svg.node();
  }

  determineTransparency(year: number) {
    if (this.additionalMarkers.length === 1) {
      return 1;
    }

    const [startMarker, endMarker] = this.additionalMarkers.sort();

    if (year < Number(startMarker) || year > Number(endMarker)) {
      return 0.25;
    } else {
      return 1;
    }
  }

  getSVG() {
    return this.svg;
  }
}
